import { SelectChangeEvent } from '@mui/material/Select';
import { ChangeEvent, Dispatch, useEffect, useReducer } from 'react';

import { CustomInput, CustomSelect, MultipleSelect, Text } from '../../../shared/uiComponents';
import { validateName } from '../../../shared/Helpers/functions';
import { useBehaviorTreatmentModificationOptions, useIsTelehealth } from '../hook';
import { Behaviors, SignatureInput, Targets, TelehealthLocations, TimeInfo } from '../components';

import {
	ACTIONS,
	ActionProperties,
	ERRORS,
	behaviorTreatmentModificationErrorReducer,
	initialBehaviorTreatmentModificationErrorState,
} from './behaviorTreatmentModificationReducer';
import { useSignatureDate, useSoapNoteFieldDisabled } from '../helpers';

import { useSelector } from '../../../redux/store';
import { UpdateBehaviorTreatmentModificationProperties } from '../../../redux/API/ClientAPIHelpers/soapNoteProperties';

const BehaviorTreatmentModificationData = ({
	data,
	setData,
	save,
}: {
	data: UpdateBehaviorTreatmentModificationProperties;
	save: () => void;
	setData: Dispatch<ActionProperties>;
}) => {
	const signatureDate = useSignatureDate();
	const locationList = useSelector((state) => state.soapNote.locations);
	const errorList = useSelector((state) => state.soapNote.error?.errors);

	const isTelehealth = useIsTelehealth(data.locationId);
	const { getFieldDisabled } = useSoapNoteFieldDisabled();
	const { barriersList, interventionsList, reinforcersList, responseToInterventionList, participantsList } =
		useBehaviorTreatmentModificationOptions();

	const [errors, setErrors] = useReducer(
		behaviorTreatmentModificationErrorReducer,
		initialBehaviorTreatmentModificationErrorState,
	);

	useEffect(() => {
		if (data.participantResponses.length) return;
		const payload = participantsList.filter((x) => x.isDefault).map((y) => y.id);
		setData({ type: ACTIONS.setParticipants, payload });
	}, [participantsList, data, setData]);

	useEffect(() => {
		if (!errorList) return;
		const list = Object.keys(errorList);
		list.forEach((id) => setErrors({ type: ERRORS[id], payload: true }));
	}, [errorList]);

	const locationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setLocationId });
		if (!errors.LocationId) return;
		setErrors({ payload: false, type: ERRORS.LocationId });
	};

	const interventionHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setIntervention });
		if (!errors.BehaviorTreatmentModificationInterventionResponses) return;
		setErrors({
			payload: false,
			type: ERRORS.BehaviorTreatmentModificationInterventionResponses,
		});
	};

	const responseToIntervationHandler = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setResponseToIntervation });
		if (!errors.ResponseToIntervention) return;
		setErrors({ payload: false, type: ERRORS.ResponseToIntervention });
	};

	const reinforcementHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setReinforcement });
		if (!errors.ReinforcementResponses) return;
		setErrors({ payload: false, type: ERRORS.ReinforcementResponses });
	};

	const barriersHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ payload: value, type: ACTIONS.setBarriers });
		if (!errors.BarrierToTreatmentResponses) return;
		setErrors({ payload: false, type: ERRORS.BarrierToTreatmentResponses });
	};

	const goalAddressedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setGoalsAddressed });
		if (!errors.GoalsAddressed) return;
		setErrors({ payload: false, type: ERRORS.GoalsAddressed });
	};

	const dataAnalyzedHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setDataAnalyzed });
		if (!errors.DataAnalyzed) return;
		setErrors({ payload: false, type: ERRORS.DataAnalyzed });
	};

	const treatmentPlanUpdatesHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setTreatmentPlanUpdates });
		if (!errors.TreatmentPlanUpdates) return;
		setErrors({ payload: false, type: ERRORS.TreatmentPlanUpdates });
	};

	const malaptiveBehaviorsHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setMaladaptiveBehaviorsObserved });
		if (!errors.MaladaptiveBehaviorsObserved) return;
		setErrors({ payload: false, type: ERRORS.MaladaptiveBehaviorsObserved });
	};

	const sessionSummaryHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setSessionSummary });
		if (!errors.SessionSummary) return;
		setErrors({ payload: false, type: ERRORS.SessionSummary });
	};

	const signatureHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value.toUpperCase(), type: ACTIONS.setSignature });
		if (!errors.Signature) return;
		setErrors({ payload: false, type: ERRORS.Signature });
	};

	const validateSignatureHandler = () => {
		if (validateName(data.signature)) {
			save();

			return;
		}
		setErrors({ payload: true, type: ERRORS.Signature });
	};

	const participantsHandler = (event: SelectChangeEvent<string[]>) => {
		const { value } = event.target;
		if (typeof value === 'string') return;
		setData({ type: ACTIONS.setParticipants, payload: value });
		if (!errors.ParticipantResponses) return;
		setErrors({ payload: false, type: ERRORS.ParticipantResponses });
	};

	const barrierToTreatmentOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setBarrierToTreatmentOther });
		if (!errors.BarrierToTreatmentOther) return;
		setErrors({ payload: false, type: ERRORS.BarrierToTreatmentOther });
	};

	const participantOtherHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target;
		setData({ payload: value, type: ACTIONS.setBehaviorTreatmentModificationParticipantOther });
		if (!errors.BehaviorTreatmentModificationParticipantOther) return;
		setErrors({ payload: false, type: ERRORS.ParticipantOther });
	};

	const isOtherSelectedForBarriersToTreatment = data.barrierToTreatmentResponses.find(
		(x) => barriersList.find((y) => y.id === x)?.text === 'Other',
	);

	const isOtherSelectedForParticipants = data.participantResponses.find(
		(x) => participantsList.find((y) => y.id === x)?.text === 'Other',
	);

	return (
		<div>
			<TimeInfo disabled={getFieldDisabled('startTime')} save={save} />
			<CustomSelect
				label="Location:"
				data={locationList}
				value={data.locationId ? data.locationId : ''}
				setValue={locationHandler}
				className="marginBottom16"
				error={errors.LocationId}
				disabled={getFieldDisabled('locationId')}
				onBlur={save}
			/>
			{isTelehealth && (
				<TelehealthLocations
					setData={setData}
					data={data}
					disabled={getFieldDisabled('clientTelehealthLocationId')}
					save={save}
				/>
			)}
			<MultipleSelect
				label="Session Participants:"
				data={participantsList}
				initialValue={data.participantResponses}
				setValue={participantsHandler}
				error={errors.ParticipantResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('participantResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForParticipants && (
				<CustomInput
					label="If other, please describe: "
					value={data.behaviorTreatmentModificationParticipantOther}
					onChange={participantOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('behaviorTreatmentModificationParticipantOther')}
					onBlur={save}
					error={errors.BehaviorTreatmentModificationParticipantOther}
				/>
			)}
			<MultipleSelect
				label="Intervention in Session:"
				data={interventionsList}
				initialValue={data.behaviorTreatmentModificationInterventionResponses}
				setValue={interventionHandler}
				error={errors.BehaviorTreatmentModificationInterventionResponses}
				className="marginBottom16"
				disabled={getFieldDisabled('behaviorTreatmentModificationInterventionResponses')}
				onBlur={save}
			/>
			<CustomSelect
				label="Response to intervention: "
				data={responseToInterventionList}
				value={`${data.responseToIntervention}`}
				setValue={responseToIntervationHandler}
				className="marginBottom16"
				error={errors.ResponseToIntervention}
				disabled={getFieldDisabled('responseToIntervention')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Reinforcers used during session:"
				initialValue={data.reinforcementResponses}
				data={reinforcersList}
				setValue={reinforcementHandler}
				className="marginBottom16"
				error={errors.ReinforcementResponses}
				disabled={getFieldDisabled('reinforcementResponses')}
				onBlur={save}
			/>
			<MultipleSelect
				label="Barriers to Treatment:"
				data={barriersList}
				initialValue={data.barrierToTreatmentResponses}
				setValue={barriersHandler}
				className="marginBottom16"
				error={errors.BarrierToTreatmentResponses}
				disabled={getFieldDisabled('barrierToTreatmentResponses')}
				onBlur={save}
			/>
			{isOtherSelectedForBarriersToTreatment && (
				<CustomInput
					label="If other, please describe: "
					value={data.barrierToTreatmentOther}
					onChange={barrierToTreatmentOtherHandler}
					className="marginBottom16"
					multiline
					disabled={getFieldDisabled('barrierToTreatmentOther')}
					onBlur={save}
					error={errors.BarrierToTreatmentOther}
				/>
			)}
			<CustomInput
				label="Goals addressed:"
				value={data.goalsAddressed}
				onChange={goalAddressedHandler}
				multiline
				className="marginBottom16"
				error={errors.GoalsAddressed}
				disabled={getFieldDisabled('goalsAddressed')}
				onBlur={save}
			/>
			<CustomInput
				label="Data analyzed:"
				value={data.dataAnalyzed}
				onChange={dataAnalyzedHandler}
				multiline
				className="marginBottom16"
				error={errors.DataAnalyzed}
				disabled={getFieldDisabled('dataAnalyzed')}
				onBlur={save}
			/>
			<CustomInput
				label="Updates to treatment plan:"
				value={data.treatmentPlanUpdates}
				onChange={treatmentPlanUpdatesHandler}
				multiline
				className="marginBottom16"
				error={errors.TreatmentPlanUpdates}
				disabled={getFieldDisabled('treatmentPlanUpdates')}
				onBlur={save}
			/>
			<CustomInput
				label="Maladaptive behaviors observed"
				value={data.maladaptiveBehaviorsObserved}
				onChange={malaptiveBehaviorsHandler}
				multiline
				className="marginBottom16"
				error={errors.MaladaptiveBehaviorsObserved}
				disabled={getFieldDisabled('maladaptiveBehaviorsObserved')}
				onBlur={save}
			/>
			<Text title="Session Details" type="h4" font="header" fontWeight="semiBold" className="marginBottom16 title" />
			<Targets />
			<Behaviors />
			<CustomInput
				label="Session Summary: "
				value={data.sessionSummary}
				onChange={sessionSummaryHandler}
				className="marginBottom8"
				multiline
				error={errors.SessionSummary}
				disabled={getFieldDisabled('sessionSummary')}
				onBlur={save}
			/>
			<SignatureInput
				value={data.signature}
				error={errors.Signature}
				setValue={signatureHandler}
				disabled={getFieldDisabled('signature')}
				onBlur={validateSignatureHandler}
			/>
			<div>
				<Text title="Date/time of signature:" type="psm" fontWeight="semiBold" />
				<Text title={signatureDate} type="psm" />
			</div>
		</div>
	);
};

export default BehaviorTreatmentModificationData;
